/**
 * Subscription Settings Component
 *
 * Displays subscription plan details, usage, and tier comparison.
 * Moved from standalone /subscription page into Settings.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { subscriptionApi } from '@services/api/subscription';
import type { Subscription, UsageStats, SubscriptionTierInfo } from '@types';
import { getUsagePercentage, isUnlimitedTier } from '@/types/subscription';
import CurrentPlanCard from '@components/subscription/CurrentPlanCard';
import UsageMeter from '@components/subscription/UsageMeter';
import TierComparison from '@components/subscription/TierComparison';
import UpgradeModal from '@components/subscription/UpgradeModal';

export function SubscriptionSettings() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subscriptionData, usageData, tiersData] = await Promise.all([
        subscriptionApi.getCurrentSubscription(),
        subscriptionApi.getUsageStats(),
        subscriptionApi.getTiers(),
      ]);

      setSubscription(subscriptionData);
      setUsageStats(usageData);
      setTiers(tiersData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load subscription data'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = (tier: string) => {
    setSelectedTier(tier);
    setUpgradeModalOpen(true);
  };

  const handleUpgradeSuccess = () => {
    setUpgradeModalOpen(false);
    fetchData();
  };

  const handleCancelSuccess = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading subscription
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={fetchData}
                className="rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription || !usageStats || !usageStats.current_period) {
    return (
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Subscription data unavailable
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Unable to load your subscription details. Please try again
                later.
              </p>
            </div>
            <div className="mt-4">
              <button
                onClick={fetchData}
                className="rounded-md bg-yellow-100 px-3 py-2 text-sm font-medium text-yellow-800 hover:bg-yellow-200"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage = usageStats.current_period
    ? getUsagePercentage(
        usageStats.current_period.employees_processed,
        usageStats.current_period.employee_limit
      )
    : 0;
  const isUnlimited = usageStats.current_period
    ? isUnlimitedTier(usageStats.current_period.employee_limit)
    : false;
  const isNearLimit = !isUnlimited && usagePercentage > 90;
  const isTrial =
    subscription.status === 'ACTIVE' && subscription.tier === 'FREE';

  return (
    <div className="space-y-6">
      {/* Warning Banners */}
      {isTrial && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md bg-yellow-50 p-4"
        >
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Free Tier</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {subscription.expires_at
                    ? `Your free tier expires on ${new Date(subscription.expires_at).toLocaleDateString()}.`
                    : 'You are on the free tier.'}{' '}
                  Upgrade to unlock more employees and features.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {isNearLimit && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md bg-orange-50 p-4"
        >
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-orange-800">
                Employee Limit Warning
              </h3>
              <div className="mt-2 text-sm text-orange-700">
                <p>
                  You've used {usagePercentage.toFixed(0)}% of your employee
                  capacity. Consider upgrading to a higher tier for more
                  employees.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Current Plan */}
      <CurrentPlanCard
        subscription={subscription}
        onUpgrade={handleUpgradeClick}
        onCancelSuccess={handleCancelSuccess}
      />

      {/* Usage Stats */}
      <UsageMeter usageStats={usageStats} />

      {/* Tier Comparison */}
      <TierComparison
        tiers={tiers}
        currentTier={subscription.tier}
        onSelectTier={handleUpgradeClick}
      />

      {/* Upgrade Modal */}
      {selectedTier && (
        <UpgradeModal
          isOpen={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          currentTier={subscription.tier}
          newTier={selectedTier}
          onSuccess={handleUpgradeSuccess}
        />
      )}
    </div>
  );
}

export default SubscriptionSettings;
